/**
 * OrderCustomerSection — issue #1185
 *
 * The order's `customer` tab (#1185, moved onto the rail by #1804) — the
 * first tab, so it is what the rail opens on.
 * The order detail response embeds the customer's name, contact, value
 * metrics, address, and note. This section presents that record without
 * issuing a second `GET /api/customers/{customer_id}` request.
 *
 * Guest orders (no `customer_id`) degrade to the order's own
 * email / phone with a "guest checkout" marker — no metrics, link,
 * address, or note. An unknown linked customer renders a lightweight
 * empty line and falls back to the order's contact fields; it never blocks
 * the rest of the detail page.
 */

import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';
import { Badge, EntityLink } from '@resource-kit/ui/react';
import { formatAddressOrNull, pickDefaultCustomerAddress } from '@/lib/formatAddress';
import { AddressLink } from '@components/ui/AddressLink';
import { formatCurrency } from '@lib/utils';
import type { OrderResourceRow } from '../types';

interface OrderCustomerSectionProps {
  order: OrderResourceRow;
}

/** Split a customer's comma-separated `tags` string into trimmed, non-empty labels. */
const parseTags = (tags: string | null | undefined): string[] =>
  (tags ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

/** One contact line — label + value. Renders nothing when the value is empty. */
function ContactRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground break-all">{value}</span>
    </div>
  );
}

export function OrderCustomerSection({ order }: OrderCustomerSectionProps) {
  const { t } = useTranslation();
  const customerId = order.customer_id;
  const hasCustomer = customerId != null && String(customerId).length > 0;

  const heading = (
    <div className="flex items-center gap-2">
      <User className="h-4 w-4 text-muted-foreground" />
      <h3 className="text-sm font-semibold text-foreground">{t('order.customer.title')}</h3>
    </div>
  );

  // ─── Guest order — no linked customer ──────────────────────────────
  if (!hasCustomer) {
    const hasContact = !!(order.email || order.phone);
    return (
      <section className="rounded-md border border-border bg-surface-1 p-4">
        <div className="flex items-center justify-between gap-2">
          {heading}
          <Badge variant="secondary">{t('order.customer.guest')}</Badge>
        </div>
        <div className="mt-3 space-y-1">
          <ContactRow label={t('order.customer.email')} value={order.email} />
          <ContactRow label={t('order.customer.phone')} value={order.phone} />
          {!hasContact && (
            <div className="text-sm text-muted-foreground">{t('order.customer.noContact')}</div>
          )}
        </div>
      </section>
    );
  }

  const customer = order.customer;
  const fullName = customer
    ? [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim()
    : '';
  const defaultAddress = pickDefaultCustomerAddress(customer?.addresses);
  // #2689: an all-empty default address renders no row rather than a "—".
  const defaultAddressText = formatAddressOrNull(defaultAddress);
  const tags = parseTags(customer?.tags);

  return (
    <section className="rounded-md border border-border bg-surface-1 p-4">
      <div className="flex items-center justify-between gap-2">
        {heading}
        <EntityLink to={`/customers/${customerId}`} className="text-sm">
          {t('order.customer.viewProfile')}
        </EntityLink>
      </div>

      {customer === undefined ? (
        <div className="mt-3 space-y-2" aria-hidden="true">
          <div className="h-5 w-40 animate-pulse rounded bg-muted" />
          <div className="h-4 w-56 animate-pulse rounded bg-muted" />
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        </div>
      ) : customer === null ? (
        <div className="mt-3 space-y-1">
          <div className="text-sm text-destructive">{t('order.customer.loadError')}</div>
          {/* Fall back to the order's own contact fields so the operator
              still sees who to reach even when the customer fetch fails. */}
          <ContactRow label={t('order.customer.email')} value={order.email} />
          <ContactRow label={t('order.customer.phone')} value={order.phone} />
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="text-base font-medium text-foreground">
            {fullName || order.email || t('order.customer.unnamed')}
          </div>

          <div className="space-y-1">
            <ContactRow label={t('order.customer.email')} value={customer?.email ?? order.email} />
            <ContactRow label={t('order.customer.phone')} value={customer?.phone ?? order.phone} />
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {customer?.orders_count != null && (
              <div className="flex gap-2">
                <span className="text-muted-foreground">{t('order.customer.ordersCount')}</span>
                <span className="text-foreground font-medium">{customer.orders_count}</span>
              </div>
            )}
            {customer?.total_spent != null && (
              <div className="flex gap-2">
                <span className="text-muted-foreground">{t('order.customer.totalSpent')}</span>
                <span className="text-foreground font-medium">
                  {formatCurrency(Number(customer.total_spent), 'USD')}
                </span>
              </div>
            )}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('order.customer.tags')}</span>
              {tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {defaultAddressText !== null && (
            <div className="flex gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">{t('order.customer.address')}</span>
              <AddressLink address={defaultAddressText} />
            </div>
          )}

          {customer?.note && (
            <div className="flex gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">{t('order.customer.note')}</span>
              <span className="text-foreground">{customer.note}</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
